/**
 * Department SSE event bus.
 *
 * Typed publish/subscribe channel for SSE-pipeline progress signals. The
 * long-term goal (Phase 8 of the refactor) is to have every pipeline
 * (research, brand, geo, social, growth, ecommerce, content, social-video)
 * publish semantic domain events here instead of mutating
 * `useDepartmentStore` directly via a callbacks bag. The store then
 * subscribes once and translates events into its mutators — pipelines no
 * longer need to know the store API, which lets them move to their owning
 * features.
 *
 * This commit (Phase 8 commit 70) lands the bus + the pipeline-agnostic
 * runner observability events (`sse:request-*`, `sse:stream-*`,
 * `sse:phase-update`, `sse:progress`, `sse:error`). They are emitted by
 * `sseRunner.ts` so subscribers (analytics, debug overlays, future
 * per-pipeline store handlers) can observe stream lifecycle without
 * coupling to the runner's internals.
 *
 * Per-pipeline semantic events (e.g. `research:queries-generated`,
 * `brand:logo-options-ready`) are introduced in subsequent commits as
 * each pipeline migrates off the callbacks contract.
 */

import { createEventBus } from "@/shared/lib/events";

/** Common envelope: every event carries the pipeline name + session id so
 *  subscribers can filter without parsing the discriminator. */
interface SSEEventBase {
  pipeline: string;
  sessionId: string;
}

/** Social-video generation pipeline (`/core/coagent/social-video-generate`) —
 *  Veo video generation with prompt compile / generate / download phases.
 *  Migrated off SSECallbacks in Phase 8 commit 79. */
export type SocialVideoSSEEvent =
  | {
      type: "social-video:input-validated";
      pipeline: "social-video-generate";
      sessionId: string;
    }
  | {
      type: "social-video:prompt-compiled";
      pipeline: "social-video-generate";
      sessionId: string;
    }
  | {
      type: "social-video:video-generation-running";
      pipeline: "social-video-generate";
      sessionId: string;
      message: string;
    }
  | {
      type: "social-video:video-download";
      pipeline: "social-video-generate";
      sessionId: string;
    }
  | {
      type: "social-video:done";
      pipeline: "social-video-generate";
      sessionId: string;
      video: {
        id: string;
        source: "template" | "custom" | "brief";
        templateId?: string;
        briefId?: string;
        title: string;
        aspectRatio: "16:9" | "9:16";
        prompt: string;
        assetUrl: string;
        createdAt: string;
      };
    }
  | {
      type: "social-video:error";
      pipeline: "social-video-generate";
      sessionId: string;
      message: string;
    };

/** Content-intelligence pipeline (`/core/coagent/content-intelligence`) —
 *  keyword generation → Meta ads discovery → TikTok discovery → video
 *  analysis → content-brief synthesis. Migrated off SSECallbacks in
 *  Phase 8 commit 78. */
export type ContentIntelligenceSSEEvent =
  | {
      type: "content-intelligence:keyword-generation-running";
      pipeline: "content-intelligence";
      sessionId: string;
    }
  | {
      type: "content-intelligence:keyword-generation-complete";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:meta-discovery-running";
      pipeline: "content-intelligence";
      sessionId: string;
    }
  | {
      type: "content-intelligence:meta-discovery-complete";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:tiktok-discovery-running";
      pipeline: "content-intelligence";
      sessionId: string;
    }
  | {
      type: "content-intelligence:tiktok-discovery-complete";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:video-analysis-running";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
      progress?: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:video-analysis-complete";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:content-synthesis-running";
      pipeline: "content-intelligence";
      sessionId: string;
    }
  | {
      type: "content-intelligence:content-synthesis-complete";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:done";
      pipeline: "content-intelligence";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "content-intelligence:error";
      pipeline: "content-intelligence";
      sessionId: string;
      message: string;
      failedPhase?: string;
      details?: unknown;
    };

/** Ecommerce-metrics pipeline (`/core/coagent/ecommerce-metrics`) —
 *  unit economics + per-product priority scoring + ad-cost estimation
 *  + portfolio synthesis. Migrated off SSECallbacks in Phase 8 commit 77. */
export type EcommerceMetricsSSEEvent =
  | {
      type: "ecommerce-metrics:metrics-calculation-running";
      pipeline: "ecommerce-metrics";
      sessionId: string;
    }
  | {
      type: "ecommerce-metrics:metrics-calculation-complete";
      pipeline: "ecommerce-metrics";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce-metrics:priority-scoring-running";
      pipeline: "ecommerce-metrics";
      sessionId: string;
    }
  | {
      type: "ecommerce-metrics:priority-scoring-complete";
      pipeline: "ecommerce-metrics";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce-metrics:ad-cost-estimation-running";
      pipeline: "ecommerce-metrics";
      sessionId: string;
    }
  | {
      type: "ecommerce-metrics:ad-cost-estimation-complete";
      pipeline: "ecommerce-metrics";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce-metrics:ad-cost-synthesis-running";
      pipeline: "ecommerce-metrics";
      sessionId: string;
    }
  | {
      type: "ecommerce-metrics:ad-cost-synthesis-complete";
      pipeline: "ecommerce-metrics";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce-metrics:done";
      pipeline: "ecommerce-metrics";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce-metrics:error";
      pipeline: "ecommerce-metrics";
      sessionId: string;
      message: string;
      details?: unknown;
    };

/** Ecommerce-research pipeline (`/core/coagent/ecommerce-research`) —
 *  store-connect / product-import / brand-analysis / categorization
 *  with manual file-upload sub-phases. Migrated off SSECallbacks in
 *  Phase 8 commit 76. */
export type EcommerceSSEEvent =
  | {
      type: "ecommerce:session-named";
      pipeline: "ecommerce-research";
      sessionId: string;
      name: string;
    }
  | {
      type: "ecommerce:file-extraction-detecting-schema";
      pipeline: "ecommerce-research";
      sessionId: string;
    }
  | {
      type: "ecommerce:file-extraction-extracting-products";
      pipeline: "ecommerce-research";
      sessionId: string;
    }
  | {
      type: "ecommerce:file-extraction-preview";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:file-extraction-complete";
      pipeline: "ecommerce-research";
      sessionId: string;
      productCount: number;
    }
  | {
      type: "ecommerce:file-extraction-error";
      pipeline: "ecommerce-research";
      sessionId: string;
      message: string;
    }
  | {
      type: "ecommerce:store-connect-running";
      pipeline: "ecommerce-research";
      sessionId: string;
      shopDomain: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:store-connect-complete";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:store-connect-error";
      pipeline: "ecommerce-research";
      sessionId: string;
      message: string;
    }
  | {
      type: "ecommerce:product-import-running";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:product-import-complete";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:brand-analysis-running";
      pipeline: "ecommerce-research";
      sessionId: string;
    }
  | {
      type: "ecommerce:brand-analysis-complete";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:product-categorization-running";
      pipeline: "ecommerce-research";
      sessionId: string;
    }
  | {
      type: "ecommerce:product-categorization-complete";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:done";
      pipeline: "ecommerce-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "ecommerce:error";
      pipeline: "ecommerce-research";
      sessionId: string;
      message: string;
      details?: unknown;
    };

/** Growth research pipeline (`/core/coagent/growth-research`) — site
 *  scrape + funnel analysis + tracking-event extraction. Migrated off
 *  SSECallbacks in Phase 8 commit 75. */
export type GrowthSSEEvent =
  | {
      type: "growth:session-named";
      pipeline: "growth-research";
      sessionId: string;
      name: string;
    }
  | {
      type: "growth:website-scrape-running";
      pipeline: "growth-research";
      sessionId: string;
      url: string;
    }
  | {
      type: "growth:website-scrape-complete";
      pipeline: "growth-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "growth:website-scrape-skipped";
      pipeline: "growth-research";
      sessionId: string;
      reason: string;
    }
  | {
      type: "growth:site-analysis-running";
      pipeline: "growth-research";
      sessionId: string;
    }
  | {
      type: "growth:site-analysis-complete";
      pipeline: "growth-research";
      sessionId: string;
      data: Record<string, unknown>;
      inputUrl: string;
    }
  | {
      type: "growth:event-extraction-complete";
      pipeline: "growth-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "growth:done";
      pipeline: "growth-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "growth:error";
      pipeline: "growth-research";
      sessionId: string;
      message: string;
      details?: unknown;
    };

/** Social research pipeline (`/core/coagent/social-research`) — query
 *  plan + multi-platform collection + trend/competitor/template/calendar
 *  generation. Migrated off SSECallbacks in Phase 8 commit 74. */
export type SocialSSEEvent =
  | {
      type: "social:session-named";
      pipeline: "social-research";
      sessionId: string;
      name: string;
    }
  | {
      type: "social:query-plan-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:data-collection-progress";
      pipeline: "social-research";
      sessionId: string;
      track: string;
      completed: number;
      total: number;
    }
  | {
      type: "social:data-collection-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:distillation-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:trend-analysis-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:competitor-audit-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:template-generation-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:calendar-generation-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:brief-generation-complete";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:done";
      pipeline: "social-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "social:error";
      pipeline: "social-research";
      sessionId: string;
      message: string;
      details?: unknown;
    };

/** GEO research pipeline (`/core/coagent/geo-research`) — 6-phase
 *  AI-citation analysis and asset generation. Migrated off SSECallbacks
 *  in Phase 8 commit 73. */
export type GeoSSEEvent =
  | {
      type: "geo:session-named";
      pipeline: "geo-research";
      sessionId: string;
      name: string;
    }
  | {
      type: "geo:site-scan-running";
      pipeline: "geo-research";
      sessionId: string;
      url: string;
    }
  | {
      type: "geo:site-scan-complete";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "geo:citation-baseline-running";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
      progress: { completed: number; total: number };
      resolvedTargetQueries: string[] | null;
    }
  | {
      type: "geo:citation-baseline-complete";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
      resolvedTargetQueries: string[] | null;
    }
  | {
      type: "geo:gap-analysis-running";
      pipeline: "geo-research";
      sessionId: string;
    }
  | {
      type: "geo:gap-analysis-complete";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "geo:asset-generation-running";
      pipeline: "geo-research";
      sessionId: string;
    }
  | {
      type: "geo:asset-generation-complete";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "geo:entity-authority-running";
      pipeline: "geo-research";
      sessionId: string;
    }
  | {
      type: "geo:entity-authority-complete";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "geo:implementation-plan-running";
      pipeline: "geo-research";
      sessionId: string;
    }
  | {
      type: "geo:implementation-plan-complete";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "geo:done";
      pipeline: "geo-research";
      sessionId: string;
      data: Record<string, unknown>;
      resolvedTargetQueries: string[] | null;
    }
  | {
      type: "geo:error";
      pipeline: "geo-research";
      sessionId: string;
      message: string;
      details?: unknown;
    };

/** Brand research pipeline (`/core/coagent/brand-research`) — website
 *  scrape + multi-source brand intelligence + synthesis + brand-config
 *  generation + logo idle gate. Migrated off SSECallbacks in Phase 8
 *  commit 72. Each event carries the raw server `data` so the subscriber
 *  can hydrate thought-events via `toThoughtEvent`. */
export type BrandSSEEvent =
  | {
      type: "brand:session-named";
      pipeline: "brand-research";
      sessionId: string;
      name: string;
    }
  | {
      type: "brand:website-scrape-running";
      pipeline: "brand-research";
      sessionId: string;
      url: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:website-scrape-complete";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:website-scrape-skipped";
      pipeline: "brand-research";
      sessionId: string;
      reason: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:query-generation-running";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:query-generation-complete";
      pipeline: "brand-research";
      sessionId: string;
      queryCount: number;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:collection-running";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
      progress: Record<string, unknown> | undefined;
    }
  | {
      type: "brand:collection-complete";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:distillation-running";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:distillation-complete";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:synthesis-running";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:synthesis-complete";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:brand-generation-running";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:brand-generation-complete";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:logo-generation-idle";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:done";
      pipeline: "brand-research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  | {
      type: "brand:error";
      pipeline: "brand-research";
      sessionId: string;
      message: string;
      details?: unknown;
    }
  | {
      type: "brand:stream-ended-incomplete";
      pipeline: "brand-research";
      sessionId: string;
    };

/** Research pipeline (`/core/coagent/research`) — initial GEO research +
 *  cross-engine query collection + synthesis. Migrated off SSECallbacks in
 *  Phase 8 commit 71. */
export type ResearchSSEEvent =
  /** Server auto-named the session from `brandInput`. */
  | {
      type: "research:session-named";
      pipeline: "research";
      sessionId: string;
      name: string;
    }
  /** Phase 1 done: prompt generator emitted N research queries. */
  | {
      type: "research:queries-generated";
      pipeline: "research";
      sessionId: string;
      queryPlan: unknown;
      count: number;
    }
  /** Phase 2 in progress: data collection from search engines. */
  | {
      type: "research:data-collection-progress";
      pipeline: "research";
      sessionId: string;
      track: string;
      completed: number;
      total: number;
    }
  /** Phase 2 done: data collection settled. `data` is the raw server
   *  payload, kept loosely typed because the shape is owned by the
   *  pipeline contract (see core `domains/research`). */
  | {
      type: "research:data-collection-complete";
      pipeline: "research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  /** Phase 3 done: distillation extracted findings + competitor signals. */
  | {
      type: "research:distillation-complete";
      pipeline: "research";
      sessionId: string;
      findingsCount: number;
      sections: number;
    }
  /** Phase 4 done: value props + content + ads plan synthesised. */
  | {
      type: "research:synthesis-complete";
      pipeline: "research";
      sessionId: string;
    }
  /** Pipeline finished. `data` is the full server payload, translated
   *  into store state via `mapResearchDoneData`. */
  | {
      type: "research:done";
      pipeline: "research";
      sessionId: string;
      data: Record<string, unknown>;
    }
  /** Pipeline failed. `message` is the rendered text;  `details` carries
   *  any structured info the server returned. */
  | {
      type: "research:error";
      pipeline: "research";
      sessionId: string;
      message: string;
      details?: unknown;
    };

/**
 * Pipeline-agnostic events emitted by `sseRunner.ts`. Subscribers can use
 * these to observe pipeline lifecycle (start, stream open, phase change,
 * close, error) without owning the per-pipeline data semantics.
 */
export type DepartmentSSEEvent =
  | ResearchSSEEvent
  | BrandSSEEvent
  | GeoSSEEvent
  | SocialSSEEvent
  | GrowthSSEEvent
  | EcommerceSSEEvent
  | EcommerceMetricsSSEEvent
  | ContentIntelligenceSSEEvent
  | SocialVideoSSEEvent
  /** POST request enqueued. Body has not been sent yet. */
  | (SSEEventBase & {
      type: "sse:request-start";
      endpoint: string;
      meta?: Record<string, unknown>;
    })
  /** POST response received (any status). `ok=false` will be followed by
   *  `sse:request-http-error`. */
  | (SSEEventBase & {
      type: "sse:request-response";
      endpoint: string;
      status: number;
      ok: boolean;
    })
  /** Network / fetch failure before a response was received. */
  | (SSEEventBase & {
      type: "sse:request-transport-error";
      endpoint: string;
      message: string;
    })
  /** Non-2xx response. `message` is the parsed error blurb. */
  | (SSEEventBase & {
      type: "sse:request-http-error";
      endpoint: string;
      status: number;
      message: string;
      details?: unknown;
    })
  /** SSE stream is open — the runner is now reading data lines. */
  | (SSEEventBase & { type: "sse:stream-connected" })
  /** SSE stream closed (graceful end OR throw). Always fires once per
   *  successful `sse:stream-connected`. */
  | (SSEEventBase & { type: "sse:stream-closed" })
  /** A `phase`/`status` change observed in the stream. Coarse-grained;
   *  per-pipeline events carry the typed payload. */
  | (SSEEventBase & {
      type: "sse:phase-update";
      phase: string;
      status: string;
    })
  /** Progress tick from a phase. `track` defaults to `phase` when the
   *  server omits it. */
  | (SSEEventBase & {
      type: "sse:progress";
      phase: string;
      track: string;
      completed: number;
      total: number;
    })
  /** A `phase: "error"` event from the server. The pipeline-specific
   *  store subscriber decides how to translate this into UI state. */
  | (SSEEventBase & {
      type: "sse:error";
      error: unknown;
    });

export const sseEvents = createEventBus<DepartmentSSEEvent>();
